import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { objectPathExists } from 'projects/common/src/app/services/utils/utilities';
import { IShift } from 'src/app/modules/core/interfaces';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { getMomentFormat, momentInstance } from 'src/app/modules/core/services/datetime.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import {
  IClock,
  IMachineProductionList,
  IPeriodList,
  IProductionTable,
  IProductionThirtyMinutes,
  LoopByMachine,
} from '../../../interfaces/production-thirty-minutes';
import { DashboardWidgetBasicComponent } from '../../dashboard-widget-basic/dashboard-widget-basic.component';

@Component({
  selector: 'app-production-every-thirty-minutes',
  templateUrl: './production-every-thirty-minutes.component.html',
  styleUrls: ['./production-every-thirty-minutes.component.scss'],
  preserveWhitespaces: true,
})
export class ProductionEveryThirtyMinutesComponent extends DashboardWidgetBasicComponent implements OnInit, OnDestroy {
  @ViewChild('tableProductionContainer', { static: false })
  private tableContainer: ElementRef;
  public dataProduction: IProductionThirtyMinutes;
  public isEvenNumberMachines = false;
  public showClock = false;
  public showData = false;
  public heightClock = 0;
  public shiftsEnd = {};
  public subtitleWidget: string;
  public currentTime;
  public interval;
  public dataTable: IProductionTable;
  public clockData: IClock;

  public displayMessage: boolean;

  constructor(
    public booleanBarService: BooleanStateService,
    public communication: CommunicationService,
    public parameterService: ParameterHandlerService,
    public cacheService: CacheService,
    public machineContext: MachineContextService,
    private languageService: LanguageService,
    protected screenViewService: ScreenViewService,
    private datePipe: DatePresentationPipe,
  ) {
    super(booleanBarService, communication, parameterService, cacheService, machineContext, screenViewService);
    this.initState();
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  private initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
      hasMachineType: true,
      hasZone: true,
    };
  }

  /**
   * Component onInit.
   */
  public ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * build view to reload when date is changed
   *
   * @param values default value
   */
  public buildView(values: any): void {
    super.buildView(values);
    this.getData(values);
  }

  /**
   * Get data from API
   */
  private getData(prodThirtyMinutes: IProductionThirtyMinutes): void {
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const date = this.parameterService.parameters.item('date').value;
    const shiftName = this.parameterService.parameters.item('shift').value as IShift;
    const user = this.parameterService.parameters.item('user-data').value;

    this.subtitleWidget = `${this.languageService.message('company')}: ${user.company.name},
    ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
    ${this.languageService.message('date')}: ${this.datePipe.transform(date)},
    ${this.languageService.message('shift_')}: ${shiftName.name}`;

    if (
      prodThirtyMinutes &&
      Object.entries(prodThirtyMinutes).length &&
      prodThirtyMinutes.periodList.length &&
      prodThirtyMinutes.machineList.length &&
      prodThirtyMinutes.machineProductionList.length
    ) {
      this.displayMessage = false;
      this.dataProduction = prodThirtyMinutes;
      this.isEvenNumberMachines = this.dataProduction.machineList.length % 2 === 0;
      this.buildTable();

      this.shiftsEnd = {};
      this.dataProduction.periodList.forEach((period) => {
        const shift = { shiftName: period.shiftName, periodStartTime: period.periodStartTime, periodEndTime: period.periodEndTime };
        this.shiftsEnd[shift.shiftName] = shift;
      });

      this.setClock();
      this.interval = setInterval(() => {
        this.setClock();
      }, 1000);
    } else {
      // No hay datos útiles, activar el sistema de noData del wrapper
      this.displayMessage = true;
      this.chartWithoutData$.next(true);
      this.noData.emit(true);
    }
  }

  /**
   * Function to get loops by machine.
   */
  private getLoopsHHMachine(machineId: number): IMachineProductionList[] {
    return this.dataProduction.machineProductionList.filter((machine) => machine.machineId === machineId);
  }

  /**
   * Function to set clock on table.
   */
  private setClock(): void {
    const currentTime = momentInstance().toString();
    if (this.dataProduction.periodList.length) {
      const firstPeriod = this.dataProduction.periodList[0].periodStartTime;
      const lastPeriodEnd = this.dataProduction.periodList[this.dataProduction.periodList.length - 1].periodEndTime;
      const lastPeriodStart = this.dataProduction.periodList[this.dataProduction.periodList.length - 1].periodStartTime;
      const isLastPeriod = Date.parse(currentTime) > Date.parse(lastPeriodStart) && Date.parse(currentTime) <= Date.parse(lastPeriodEnd);

      if (objectPathExists(this.tableContainer, 'nativeElement')) {
        const tableRef = this.tableContainer.nativeElement.children[1];
        const colRef = objectPathExists(tableRef, 'firstElementChild.firstElementChild.firstElementChild.firstElementChild', true);
        if (!colRef) {
          this.showClock = false;
          return;
        }
        const colWidth = colRef.getBoundingClientRect().width;
        const colHeight = colRef.getBoundingClientRect().height;

        const colPadding = parseFloat(getComputedStyle(colRef, null).paddingLeft) + parseFloat(getComputedStyle(colRef, null).paddingRight);
        const growFactor = colWidth / 30;

        this.heightClock = (this.dataProduction.machineList.length + 2) * colHeight;
        this.clockData.heightClock = this.heightClock;

        this.currentTime = getMomentFormat(currentTime, 'HH:mm').split(':');
        const index = this.findCurrentPeriodPosition(currentTime);
        if (index !== -1) {
          const currentPeriodSplitStart = getMomentFormat(this.dataProduction.periodList[index].periodStartTime, 'HH:mm').split(':');
          const positionCalculation =
            colPadding + index * colWidth + Math.abs(+this.currentTime[1] - +currentPeriodSplitStart[1]) * growFactor;
          this.clockData = {
            ...this.clockData,
            positionClock: positionCalculation - (isLastPeriod ? colPadding : 0),
          };
        }
        const positionCalculationBg = this.positionCalculationBgClock(lastPeriodStart, lastPeriodEnd, colWidth, colPadding);
        this.clockData = {
          ...this.clockData,
          positionClockBg: positionCalculationBg,
          currentTime: this.currentTime,
          widthClockBg: colWidth,
        };
        if (Date.parse(currentTime) < Date.parse(firstPeriod) || Date.parse(currentTime) >= Date.parse(lastPeriodEnd)) {
          this.showClock = false;
        } else {
          this.showClock = true;
        }
      } else {
        this.showClock = false;
      }
    } else {
      this.showClock = false;
    }
  }

  /**
   * Function to load data for table.
   */
  private buildTable(): void {
    // Header Table
    if (!objectPathExists(this.dataTable, 'tHead.header.length', true)) {
      this.dataTable.tHead.header = [
        this.languageService.message('machines-column').toUpperCase(),
        this.languageService.message('total').toUpperCase(),
      ];
    }
    this.dataTable.tHead.rangePeriods = this.dataProduction.periodList.map((period) => {
      return {
        periodId: period.periodId,
        periodStartTime: period.periodStartTime,
        periodEndTime: period.periodEndTime,
      };
    });

    // Body Table
    this.dataTable.tBody = this.dataProduction.machineList.map((machine) => {
      const loopsByMachineId: IMachineProductionList[] = this.getLoopsHHMachine(machine.machineId);
      let accum = 0;
      const loopsByMachine: LoopByMachine[] = this.dataTable.tHead.rangePeriods.map((loopPeriod: IPeriodList) => {
        const loop = loopsByMachineId.find((lbm) => lbm.machineId === machine.machineId && lbm.periodId === loopPeriod.periodId);
        accum += loop ? loop.productionInPeriod : 0;
        return {
          loopsMachine: loop ? loop.productionInPeriod : 0,
          accumLoops: loop ? loop.productionAccumulated : accum,
        };
      });
      return {
        ...machine,
        loopsByMachine,
        loops: accum,
      };
    });

    // Footer Table
    let totalLoops = 0;
    this.dataProduction.machineList.forEach((machine) => {
      const productionByMachine = this.dataProduction.machineProductionList.filter(
        (production) => production.machineId === machine.machineId,
      );
      totalLoops +=
        productionByMachine && productionByMachine.length ? productionByMachine[productionByMachine.length - 1].productionAccumulated : 0;
    });

    // Verificar si hay datos después de calcular el total
    if (totalLoops === 0) {
      this.chartWithoutData$.next(true);
    } else {
      this.chartWithoutData$.next(false);
    }

    this.dataTable.tFooter.totalValue = [this.languageService.message('total').toUpperCase(), totalLoops];
    this.dataTable.tFooter.totalLoopsPeriod = this.dataProduction.periodList.map((period) => {
      const productionByPeriod = this.dataProduction.machineProductionList.filter((production) => production.periodId === period.periodId);
      return productionByPeriod.reduce((acc, curr) => acc + curr.productionInPeriod, 0);
    });
    this.dataTable.shiftId = String((this.parameterService.parameters.item('shift').value as IShift).shiftId);
    this.dataTable.date = String(this.parameterService.parameters.item('date').value);
  }

  /**
   * Calculates the end of shift position
   */
  private positionCalculationBgClock(lastPeriodStart: string, lastPeriodEnd: string, colWidth: number, colPaddinLeft: number): number[] {
    this.clockData.positionClockBg = [];
    let positionBgCalculation = [];
    Object.entries(this.shiftsEnd).forEach((shift: any) => {
      const start = shift[1].periodStartTime;
      const end = shift[1].periodEndTime;
      const isLast = Date.parse(start) >= Date.parse(lastPeriodStart) && Date.parse(end) <= Date.parse(lastPeriodEnd);
      const index = this.dataProduction.periodList.findIndex((period) => period.periodStartTime === start && period.periodEndTime === end);
      positionBgCalculation = [...positionBgCalculation, isLast ? index * colWidth : colPaddinLeft + index * colWidth];
    });
    return positionBgCalculation;
  }

  /**
   * Find current period position
   */
  private findCurrentPeriodPosition(currentTime: string): number {
    return this.dataProduction.periodList.findIndex((periods) => {
      return Date.parse(currentTime) >= Date.parse(periods.periodStartTime) && Date.parse(currentTime) <= Date.parse(periods.periodEndTime);
    });
  }

  /**
   * Init state variables.
   */
  private initState(): void {
    this.dataProduction = {
      machineList: [],
      machineProductionList: [],
      periodList: [],
    };

    this.dataTable = {
      tHead: {
        header: [],
        rangePeriods: [],
      },
      tBody: [],
      tFooter: {
        totalLoopsPeriod: [],
        totalValue: [],
      },
      date: '',
      shiftId: '',
    };

    this.clockData = {
      currentTime: [],
      heightClock: 0,
      positionClock: 0,
      positionClockBg: [],
      widthClockBg: 0,
    };
    this.isEvenNumberMachines = false;
    this.showClock = false;
    this.heightClock = 0;
    this.displayMessage = false; // Revertir a false ya que el wrapper maneja los mensajes
  }

  /**
   * On destroy component.
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    clearInterval(this.interval);
  }
}
